/**
 * VISTOERA_COMPETITIVE_CONTENT_REAL_DATA_UI_READINESS_V1
 *
 * Pure display helpers that map real Capture / Media / AIAnalysis contracts
 * into tenant-safe product copy. Never invents a second semantic schema —
 * prefers `analysis.semantic_analysis_v1` when present.
 */

import type {
  AnalysisSnapshotOut,
  AnalysisStageOut,
  MediaAnalysisSnapshotOut,
  ReferenceContentDetail,
  SemanticAnalysisV1,
} from '../api'
import { SAAS_API_BASE_URL_EXPORT as SAAS_API_BASE_URL } from '../api'
import { resolveMediaUrlAgainstOrigin } from '../mediaUrlCore'

const SAAS_API_ORIGIN = SAAS_API_BASE_URL.replace(/\/api\/saas$/, '')

const COMPONENT_LABELS: Record<string, string> = {
  probe: '媒体探测',
  asr: '转写',
  scene: '镜头拆解',
  keyframe: '关键帧',
  motion: '运动分析',
  ocr: '文字识别',
}

const COMPONENT_STATUS_LABELS: Record<string, string> = {
  PENDING: '等待中',
  RUNNING: '进行中',
  SUCCEEDED: '已完成',
  FAILED: '失败',
  SKIPPED: '已跳过',
  succeeded: '已完成',
  failed: '失败',
  pending: '等待中',
  running: '进行中',
}

export function formatClockRange(startMs: number, endMs: number): string {
  return `${formatClock(startMs)} - ${formatClock(endMs)}`
}

export function formatClock(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

export function componentStatusLabel(raw: string | null | undefined): string {
  if (!raw) return '未知'
  return COMPONENT_STATUS_LABELS[raw] || '处理中'
}

export function componentNameLabel(key: string): string {
  return COMPONENT_LABELS[key] || key
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null
}

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return value
    .map((item) => {
      if (typeof item === 'string') return item
      const rec = asRecord(item)
      if (!rec) return null
      return asString(rec.pattern) || asString(rec.summary) || asString(rec.text) || asString(rec.label) || asString(rec.claim)
    })
    .filter((s): s is string => Boolean(s))
}

export function getSemanticV1(analysis: AnalysisSnapshotOut | null | undefined): SemanticAnalysisV1 | null {
  if (!analysis) return null
  const direct = analysis.semantic_analysis_v1
  if (direct && typeof direct === 'object') return direct
  return null
}

export type MediaOverviewFacts = {
  durationLabel: string | null
  dimensionsLabel: string | null
  contentTypeLabel: string | null
  transcriptStatusLabel: string | null
  shotCount: number
  keyframeCount: number
  mediaStatusLabel: string | null
}

export function buildMediaOverviewFacts(detail: ReferenceContentDetail): MediaOverviewFacts {
  const media = detail.media_analysis
  const capture = detail.capture
  const probe = asRecord(media?.media_probe) || {}

  const durationMs =
    detail.duration_ms ??
    capture?.media_duration_ms ??
    (typeof probe.duration_ms === 'number' ? probe.duration_ms : null) ??
    (typeof probe.durationMs === 'number' ? probe.durationMs : null)

  const width =
    capture?.media_width ??
    (typeof probe.width === 'number' ? probe.width : null) ??
    (typeof probe.media_width === 'number' ? probe.media_width : null)
  const height =
    capture?.media_height ??
    (typeof probe.height === 'number' ? probe.height : null) ??
    (typeof probe.media_height === 'number' ? probe.media_height : null)

  const contentType = media?.content_type || detail.content_type
  const contentTypeLabel =
    contentType === 'VIDEO' ? '视频' : contentType === 'IMAGE_POST' ? '图文' : contentType === 'MIXED' ? '混合' : contentType

  const componentStatus = media?.component_status || {}
  const transcriptRaw = componentStatus.asr || componentStatus.transcript || null

  const shotCount = detail.shots?.length ?? 0
  const keyframeCount = (detail.shots || []).filter((s) => Boolean(s.representative_frame_url)).length

  let mediaStatusLabel: string | null = null
  if (!media) mediaStatusLabel = '等待分析'
  else if (media.status === 'PENDING' || media.status === 'RUNNING') mediaStatusLabel = '分析中'
  else if (media.status === 'PARTIAL') mediaStatusLabel = '部分完成'
  else if (media.status === 'READY') mediaStatusLabel = '分析完成'
  else if (media.status === 'FAILED') mediaStatusLabel = '分析失败'

  return {
    durationLabel: durationMs != null ? formatClock(durationMs) : null,
    dimensionsLabel: width != null && height != null ? `${width} × ${height}` : null,
    contentTypeLabel,
    transcriptStatusLabel: transcriptRaw ? componentStatusLabel(transcriptRaw) : media ? '暂无' : null,
    shotCount,
    keyframeCount,
    mediaStatusLabel,
  }
}

export function isMediaLayerVisible(media: MediaAnalysisSnapshotOut | null | undefined): boolean {
  if (!media) return false
  return media.status === 'READY' || media.status === 'PARTIAL'
}

export function aiPendingCopy(detail: ReferenceContentDetail): string {
  const analysis = detail.analysis
  const recovery = detail.recovery
  const progress = detail.analysis_progress
  if (analysis?.status === 'failed') {
    return analysis.error_message || 'AI 深度分析暂未完成，你可以稍后重新分析。'
  }
  if (analysis?.status === 'running' || analysis?.status === 'pending') {
    return '正在进行 AI 深度分析，已完成的结果可以先查看。'
  }
  if (progress?.ai_failed || recovery?.execution_state === 'FAILED') {
    return 'AI 深度分析未完成，可以重新分析。'
  }
  if (recovery?.active_task_in_flight) {
    if (recovery.execution_state === 'QUEUED') {
      return 'AI 深度分析正在排队…'
    }
    return '正在进行 AI 深度分析，已完成的结果可以先查看。'
  }
  if (isMediaLayerVisible(detail.media_analysis) && !analysis) {
    return '基础分析已经完成，正在等待 AI 深度分析。'
  }
  if (detail.status === 'partial') return '正在进行 AI 深度分析，已完成的结果可以先查看。'
  if (detail.status === 'analyzing') return '正在进行 AI 深度分析，已完成的结果可以先查看。'
  return 'AI 深度分析中'
}

export type InsightSection = { titleKey: string; body?: string; items?: string[] }

export function buildInsightSections(analysis: AnalysisSnapshotOut): InsightSection[] {
  const semantic = getSemanticV1(analysis)
  if (semantic) {
    const sections: InsightSection[] = []
    const exec = asRecord(semantic.executive_summary)
    if (exec) {
      const lines = [asString(exec.one_liner), asString(exec.core_message), asString(exec.content_goal)].filter(Boolean) as string[]
      if (lines.length) sections.push({ titleKey: 'contentSummary', items: lines })
    }
    const hook = asRecord(semantic.hook_analysis)
    if (hook) {
      const body = [asString(hook.hook_summary), asString(hook.why_it_works)].filter(Boolean).join('\n')
      if (body) sections.push({ titleKey: 'hook', body })
    }
    const narrative = Array.isArray(semantic.narrative_structure) ? semantic.narrative_structure : []
    if (narrative.length) {
      sections.push({ titleKey: 'structure',
        items: narrative.map((stage, i) => {
          const rec = asRecord(stage)
          if (!rec) return `阶段 ${i + 1}`
          const label = asString(rec.stage_type) || asString(rec.summary) || `阶段 ${i + 1}`
          const summary = asString(rec.summary)
          return summary && summary !== label ? `${label}：${summary}` : label
        }),
      })
    }
    const script = asRecord(semantic.script_analysis)
    if (script) {
      const items = [
        asString(script.opening) ? `开场：${asString(script.opening)}` : null,
        asString(script.cta) ? `行动号召：${asString(script.cta)}` : null,
        asString(script.reusable_script_pattern) ? `可复用模式：${asString(script.reusable_script_pattern)}` : null,
        asString(script.speaking_style) ? `表达风格：${asString(script.speaking_style)}` : null,
      ].filter(Boolean) as string[]
      if (items.length) sections.push({ titleKey: 'script', items })
    }
    const visual = asRecord(semantic.visual_semantic_analysis)
    if (visual) {
      const items = [
        asString(visual.visual_style),
        asString(visual.subject_presentation),
        asString(visual.pacing_interpretation),
        asString(visual.dominant_visual_language),
      ].filter(Boolean) as string[]
      if (items.length) sections.push({ titleKey: 'visualStyle', items })
    }
    const rhythm = asRecord(semantic.rhythm_and_emotion)
    if (rhythm) {
      const items = [
        asString(rhythm.pacing) ? `节奏：${asString(rhythm.pacing)}` : null,
        ...asStringList(rhythm.emotional_progression).map((x) => `情绪：${x}`),
      ].filter(Boolean) as string[]
      if (items.length) sections.push({ titleKey: 'rhythm', items })
    }
    const patterns = asStringList(semantic.reusable_patterns)
    if (patterns.length) sections.push({ titleKey: 'patterns', items: patterns })
    const adapt = asRecord(semantic.originality_adaptation_strategy) || semantic.originality_adaptation_strategy
    if (typeof adapt === 'string' && adapt.trim()) sections.push({ titleKey: 'adaptation', body: adapt.trim() })
    else if (asRecord(adapt)) {
      const items = Object.values(asRecord(adapt)!)
        .flatMap((v) => (typeof v === 'string' ? [v] : asStringList(v)))
        .filter(Boolean)
      if (items.length) sections.push({ titleKey: 'adaptation', items })
    }
    const opportunities = asStringList(semantic.content_opportunities)
    if (opportunities.length) sections.push({ titleKey: 'opportunities', items: opportunities })
    const notes = asStringList(semantic.model_notes)
    if (notes.length) sections.push({ titleKey: 'risks', items: notes })
    const why = asRecord(semantic.why_it_works)
    if (why) {
      const items = [...asStringList(why.mechanisms), asString(why.audience_psychology)].filter(Boolean) as string[]
      if (items.length) sections.push({ titleKey: 'whyEffective', items })
    }
    return sections
  }

  // Legacy flat payload fallback (pre-semantic Skill rows).
  const sections: InsightSection[] = []
  if (analysis.summary) sections.push({ titleKey: 'contentSummary', body: analysis.summary })
  if (typeof analysis.hook_analysis === 'string' && analysis.hook_analysis) {
    sections.push({ titleKey: 'hook', body: analysis.hook_analysis })
  }
  if (analysis.audience_strategy) sections.push({ titleKey: 'audience', body: analysis.audience_strategy })
  if (analysis.platform_strategy) sections.push({ titleKey: 'platform', body: analysis.platform_strategy })
  if (analysis.strengths?.length) sections.push({ titleKey: 'strengths', items: analysis.strengths })
  if (analysis.weaknesses?.length) sections.push({ titleKey: 'risks', items: analysis.weaknesses })
  if (analysis.replicable_patterns?.length) {
    sections.push({ titleKey: 'patterns', items: asStringList(analysis.replicable_patterns) })
  }
  if (analysis.creative_takeaways) {
    const items = asStringList(analysis.creative_takeaways)
    if (items.length) sections.push({ titleKey: 'adaptation', items })
  }
  return sections
}

export function buildScriptStages(analysis: AnalysisSnapshotOut): Array<{
  stage: string
  start_ms?: number | null
  end_ms?: number | null
  quote?: string | null
  function: string
  expression?: string | null
  why_effective?: string | null
}> {
  const semantic = getSemanticV1(analysis)
  if (semantic) {
    const narrative = Array.isArray(semantic.narrative_structure) ? semantic.narrative_structure : []
    if (narrative.length) {
      return narrative.map((stage, i) => {
        const rec = asRecord(stage) || {}
        return {
          stage: asString(rec.stage_type) || asString(rec.summary) || `阶段 ${i + 1}`,
          start_ms: typeof rec.start_ms === 'number' ? rec.start_ms : null,
          end_ms: typeof rec.end_ms === 'number' ? rec.end_ms : null,
          quote: null,
          function: asString(rec.purpose) || asString(rec.summary) || '—',
          expression: null,
          why_effective: null,
        }
      })
    }
  }
  if (Array.isArray(analysis.script_analysis)) return analysis.script_analysis
  return []
}

export function buildHookText(analysis: AnalysisSnapshotOut): string | null {
  const semantic = getSemanticV1(analysis)
  const hook = asRecord(semantic?.hook_analysis) || asRecord(analysis.hook_analysis)
  if (hook) {
    return [asString(hook.hook_summary), asString(hook.why_it_works)].filter(Boolean).join('\n') || null
  }
  return typeof analysis.hook_analysis === 'string' ? analysis.hook_analysis : null
}

export type ObjectiveVisualFacts = {
  motionLabels: string[]
  ocrTexts: string[]
  imagePageCount: number
}

/** Layer 2 objective facts only — never dump raw metric debug blobs. */
export function buildObjectiveVisualFacts(detail: ReferenceContentDetail): ObjectiveVisualFacts {
  const media = detail.media_analysis
  const motion = asRecord(media?.motion_analysis) || {}
  const motionLabels: string[] = []
  for (const key of ['motion_type', 'camera_motion', 'pacing', 'overall_motion', 'dominant_motion']) {
    const v = asString(motion[key])
    if (v) motionLabels.push(v)
  }
  if (!motionLabels.length) {
    const summary = asString(motion.summary) || asString(motion.description)
    if (summary) motionLabels.push(summary)
  }

  const ocr = media?.ocr_results
  const ocrTexts: string[] = []
  if (Array.isArray(ocr)) {
    for (const page of ocr) {
      const rec = asRecord(page)
      const text = asString(rec?.text) || asString(rec?.ocr_text)
      if (text) ocrTexts.push(text)
    }
  } else {
    const rec = asRecord(ocr)
    if (rec) {
      const pages = Array.isArray(rec.pages) ? rec.pages : Array.isArray(rec.blocks) ? rec.blocks : []
      for (const page of pages) {
        const p = asRecord(page)
        const text = asString(p?.text) || asString(p?.ocr_text)
        if (text) ocrTexts.push(text)
      }
      const flat = asString(rec.text)
      if (flat) ocrTexts.push(flat)
    }
  }

  return {
    motionLabels: motionLabels.slice(0, 8),
    ocrTexts: ocrTexts.slice(0, 12),
    imagePageCount: detail.image_pages?.length ?? 0,
  }
}

export function buildAiVisualNotes(analysis: AnalysisSnapshotOut | null): string[] {
  if (!analysis || analysis.status !== 'succeeded') return []
  const semantic = getSemanticV1(analysis)
  const visual = asRecord(semantic?.visual_semantic_analysis) || asRecord(analysis.visual_strategy)
  if (!visual) {
    if (typeof analysis.rhythm_analysis === 'string' && analysis.rhythm_analysis) return [analysis.rhythm_analysis]
    return []
  }
  const notes = [
    asString(visual.visual_style),
    asString(visual.subject_presentation),
    asString(visual.framing_pattern),
    asString(visual.pacing_interpretation),
    asString(visual.text_overlay_usage),
    asString(visual.dominant_visual_language),
  ].filter(Boolean) as string[]
  if (Array.isArray(visual.observations)) {
    notes.push(...asStringList(visual.observations))
  }
  return notes
}

/** Resolve SaaS API paths and hosted URLs for tenant image slots. */
export function resolveHostedMediaUrl(url: string | null | undefined): string | null {
  return resolveHostedMediaUrlWithOrigin(url, SAAS_API_ORIGIN)
}

/** Testable core — never double-prefix `/api/saas` onto `SAAS_API_BASE_URL`. */
export function resolveHostedMediaUrlWithOrigin(
  url: string | null | undefined,
  apiOrigin: string,
): string | null {
  return resolveMediaUrlAgainstOrigin(url, apiOrigin)
}

/** Reject absolute local paths leaking into tenant UI image slots. */
export function isSafeHostedUrl(url: string | null | undefined): boolean {
  if (!url) return false
  if (/^(https?:)?\/\//i.test(url)) return true
  if (url.startsWith('/api/')) return true
  if (url.startsWith('/')) return true
  if (/^[a-zA-Z]:[\\/]/.test(url) || url.startsWith('\\\\') || url.startsWith('file:')) return false
  return false
}

type ReferenceUrlFields = {
  external_url?: string | null
  canonical_url?: string | null
  source_url?: string | null
  original_link_reachable?: boolean | null
}

/** Best openable original-platform URL for a reference content row. */
export function resolveReferenceExternalUrl(item: ReferenceUrlFields): string | null {
  if (item.original_link_reachable === false) return null
  const candidate = (item.external_url || item.canonical_url || item.source_url || '').trim()
  if (!candidate) return null
  return /^https?:\/\//i.test(candidate) ? candidate : null
}

type ReferenceCoverFields = {
  cover_image_url?: string | null
}

type ReferenceShotFields = {
  representative_frame_url?: string | null
}

/** Prefer persisted cover; fall back to first safe media-analysis keyframe. */
export function resolveReferenceCoverUrl(
  item: ReferenceCoverFields,
  detail?: { shots?: ReferenceShotFields[] | null } | null,
): string | null {
  if (isSafeHostedUrl(item.cover_image_url)) return resolveHostedMediaUrl(item.cover_image_url)
  const shot = detail?.shots?.find((s) => isSafeHostedUrl(s.representative_frame_url))
  return resolveHostedMediaUrl(shot?.representative_frame_url ?? null)
}

export type StageVisualState = 'pending' | 'running' | 'done' | 'failed'

export function stageVisualState(stage: AnalysisStageOut | undefined): StageVisualState {
  if (!stage) return 'pending'
  if (stage.status === 'COMPLETED') return 'done'
  if (stage.status === 'FAILED') return 'failed'
  if (stage.status === 'RUNNING') return 'running'
  return 'pending'
}

export function stageStatusGlyph(state: StageVisualState): string {
  if (state === 'done') return '✓'
  if (state === 'running') return '●'
  if (state === 'failed') return '⚠'
  return '○'
}

export function findAnalysisStage(detail: ReferenceContentDetail, id: string): AnalysisStageOut | undefined {
  return detail.analysis_stages?.find((s) => s.id === id)
}

export function tabStageForDetail(detail: ReferenceContentDetail, tab: string): AnalysisStageOut | undefined {
  const map: Record<string, string> = {
    summary: 'MEDIA_PREPARATION',
    transcript: 'TRANSCRIPTION',
    shots: 'SHOT_ANALYSIS',
    visual: 'KEYFRAME_ANALYSIS',
    script: 'AI_SCRIPT_STRUCTURE',
    insights: 'AI_INSIGHTS',
  }
  const stageId = map[tab]
  return stageId ? findAnalysisStage(detail, stageId) : undefined
}

export function capturePendingText(detail: ReferenceContentDetail): string | null {
  const capture = detail.capture
  if (!capture || capture.status === 'pending' || capture.status === 'running') {
    return '内容获取中，请稍候'
  }
  if (capture.status === 'failed' && !isMediaLayerVisible(detail.media_analysis)) {
    return detail.status_detail || '内容获取失败'
  }
  return null
}
